import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Modal,
  Radio,
  Row,
  Space,
  Spin,
  Typography,
  notification,
  theme,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { NamePath } from 'antd/es/form/interface';
import { useEffect, useState } from 'react';
import PageTitle from 'components/page-title';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { STANDARD_DAYS_MODE_LABEL, STAR_GROUP_LABEL } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import {
  ClaimLimitsConfig,
  KcDaysTier,
  KcTier,
  StarConfig,
  StarGroup,
} from 'pages/star-shared/types';
import { formatNumber } from 'pages/star-shared/utils';
import {
  CLAIM_LIMITS_DEFAULTS,
  DANCE_DEFAULTS,
  RECRUIT_DEFAULTS,
} from 'pages/star-shared/use-star-rules';

const cardBody = { body: { padding: 16 } };

const DEFAULT_CONFIG: StarConfig = {
  kcTiers: [
    { kc: 30000, star: 30 },
    { kc: 50000, star: 50 },
    { kc: 80000, star: 75 },
    { kc: 120000, star: 100 },
    { kc: 200000, star: 150 },
    { kc: 300000, star: 200 },
  ],
  kcDaysMonth: [
    { days: 8, star: 40 },
    { days: 12, star: 80 },
    { days: 16, star: 140 },
    { days: 20, star: 220 },
    { days: 24, star: 300 },
  ],
  weeklyStreak: [1, 3, 5, 10],
  standardDaysMode: 'FIXED',
  standardDaysFixed: 26,
  standardDaysFeb: 24,
  transferMonthlyCap: 300,
  smodAdjustCap: 500,
  shopDailyLimit: 2,
  orderExpireDays: 7,
  // Nhiệm vụ mở rộng — docs/nghien-cuu-diem-sao/SPEC-NHIEM-VU-MO-RONG.md §1.
  shiftOnTime: { star: 2 },
  monthPerfect: { star: 30 },
  tenure: [
    { days: 180, star: 50 },
    { days: 365, star: 100 },
  ],
  liveWeek: { days: 5, minMinutesPerDay: 120, star: 10 },
  liveMonth: { days: 20, hours: 40, minMinutesPerDay: 60, star: 30 },
  kcGrowth: {
    baseMonths: 3,
    minMonthKc: 100000,
    tiers: [
      { pct: 10, star: 30 },
      { pct: 25, star: 60 },
    ],
  },
  recruit: RECRUIT_DEFAULTS,
  dance: DANCE_DEFAULTS,
  // Giới hạn chống spam nhiệm vụ nộp tay — docs/nghien-cuu-diem-sao/10 §5.3.
  claimLimits: CLAIM_LIMITS_DEFAULTS,
};

// Nhóm dạng object: gộp từng nhóm với mặc định (máy chủ cũ chưa trả nhóm mới, hoặc thiếu trường
// con → lấy mặc định), giống StarConfigService.merge() phía backend.
const OBJECT_GROUPS = [
  'shiftOnTime',
  'monthPerfect',
  'liveWeek',
  'liveMonth',
  'kcGrowth',
  'recruit',
  'dance',
  'claimLimits',
] as const;

// claimLimits có 2 object con theo nhóm Sao: gộp thêm một cấp nữa (máy chủ trả thiếu nhóm → ô trống
// → rule required chặn Lưu).
const CLAIM_LIMIT_GROUP_KEYS = ['submissionsPerDayByGroup', 'monthlyCapByGroup'] as const;
const STAR_GROUPS: StarGroup[] = ['STAFF', 'CREATOR', 'NPC'];

function withDefaults(data: Partial<StarConfig> | null | undefined, base = DEFAULT_CONFIG) {
  const next: Record<string, any> = { ...base, ...(data || {}) };
  OBJECT_GROUPS.forEach((key) => {
    next[key] = { ...base[key], ...((data?.[key] as object) || {}) };
  });
  CLAIM_LIMIT_GROUP_KEYS.forEach((key) => {
    next.claimLimits[key] = {
      ...base.claimLimits[key],
      ...((data?.claimLimits?.[key] as object) || {}),
    };
  });
  return next as StarConfig;
}

// Máy chủ cũ (trước đợt doc 10) chưa khai `claimLimits` trong UpdateStarConfigDto mà ValidationPipe
// có forbidNonWhitelisted → gửi kèm là 400 "property claimLimits should not exist" cho MỌI lần Lưu.
// Chỉ gửi nhóm này khi GET /stars/admin/config có trả khoá đó.
type StarConfigPayload = Omit<StarConfig, 'claimLimits'> & { claimLimits?: ClaimLimitsConfig };

function withoutClaimLimits(values: StarConfig): StarConfigPayload {
  const next: StarConfigPayload = { ...values };
  delete next.claimLimits;
  return next;
}

type TierRow = Record<string, number>;

// Bỏ dòng mốc chưa nhập đủ, sắp tăng dần theo mốc.
function sortTiers<T extends object>(list: T[] | undefined, key: keyof T & string): T[] {
  return [...(list || [])]
    .filter((t) => {
      const row = t as unknown as TierRow;
      return t && typeof row[key] === 'number' && typeof row.star === 'number';
    })
    .sort((a, b) => (a as unknown as TierRow)[key] - (b as unknown as TierRow)[key]);
}

function normalize(values: StarConfig): StarConfig {
  const kcTiers = [...(values.kcTiers || [])]
    .filter((t) => t && typeof t.kc === 'number' && typeof t.star === 'number')
    .sort((a, b) => a.kc - b.kc);
  const kcDaysMonth = [...(values.kcDaysMonth || [])]
    .filter((t) => t && typeof t.days === 'number' && typeof t.star === 'number')
    .sort((a, b) => a.days - b.days);
  return {
    ...values,
    kcTiers,
    kcDaysMonth,
    weeklyStreak: (values.weeklyStreak || []).slice(0, 4).map((n) => Number(n) || 0),
    tenure: sortTiers(values.tenure, 'days'),
    kcGrowth: { ...values.kcGrowth, tiers: sortTiers(values.kcGrowth?.tiers, 'pct') },
    dance: {
      ...values.dance,
      practiceTiers: sortTiers(values.dance?.practiceTiers, 'score'),
      examTiers: sortTiers(values.dance?.examTiers, 'score'),
    },
  };
}

// Mốc (và Sao của mốc) phải tăng dần, không trùng — cùng luật với validate() phía backend.
function tierProblem<T extends object>(
  list: T[],
  key: keyof T & string,
  what: string,
  format: (value: number) => string
): string | null {
  if (!list.length) return `${what}: cần ít nhất một mốc.`;
  for (let i = 1; i < list.length; i += 1) {
    const prev = list[i - 1] as unknown as TierRow;
    const cur = list[i] as unknown as TierRow;
    if (cur[key] === prev[key]) return `${what}: mốc ${format(cur[key])} bị trùng.`;
    if (cur.star <= prev.star) {
      return `${what}: mốc ${format(cur[key])} phải được nhiều Sao hơn mốc ${format(prev[key])}.`;
    }
  }
  return null;
}

function recruitProblem(r: StarConfig['recruit']): string | null {
  if (r.retainAtDays <= r.qualifyWindowDays) {
    return 'Tuyển dụng: ngày xét "Giữ chân" phải sau hạn "Đạt chuẩn".';
  }
  if (r.qualifyLiveDays > r.qualifyWindowDays) {
    return 'Tuyển dụng: số ngày live "Đạt chuẩn" không được lớn hơn số ngày của hạn.';
  }
  if (r.retainLiveDays > r.retainAtDays - r.qualifyWindowDays) {
    return `Tuyển dụng: "Giữ chân" chỉ xét ${
      r.retainAtDays - r.qualifyWindowDays
    } ngày, số ngày live cần nhỏ hơn hoặc bằng.`;
  }
  if (r.standoutKcDays > r.standoutWindowDays) {
    return 'Tuyển dụng: số ngày đạt KC "Nổi bật" không được lớn hơn số ngày của hạn.';
  }
  return null;
}

const sameTiers = (a: KcTier[], b: KcTier[]) =>
  a.length === b.length && a.every((t, i) => t.kc === b[i].kc && t.star === b[i].star);
const sameDaysTiers = (a: KcDaysTier[], b: KcDaysTier[]) =>
  a.length === b.length && a.every((t, i) => t.days === b[i].days && t.star === b[i].star);

const numberField = (
  label: string,
  name: NamePath,
  extra?: string,
  min = 0,
  max?: number,
  unit?: string
) => (
  <Form.Item
    label={label}
    name={name}
    extra={extra}
    rules={[
      {
        required: true,
        type: 'integer',
        min,
        max,
        message: max ? `Số nguyên từ ${min} đến ${max}.` : `Số nguyên ≥ ${min}.`,
      },
    ]}
  >
    <InputNumber
      size="large"
      style={{ width: '100%' }}
      min={min}
      max={max}
      precision={0}
      addonAfter={unit}
    />
  </Form.Item>
);

const kcFormatter = (v?: number | string) =>
  v === undefined || v === null || v === '' ? '' : formatNumber(Number(v));
const kcParser = (v?: string) => Number(String(v || '').replace(/[^\d]/g, ''));

interface TierListProps {
  name: NamePath;
  /** Trường mốc của từng dòng: days | pct | score. */
  field: string;
  min: number;
  max?: number;
  step?: number;
  precision?: number;
  before?: string;
  after?: string;
  message: string;
}

// Danh sách mốc "≥ X → Y ⭐" (thêm / xoá dòng), dùng cho các mảng mốc của nhiệm vụ mở rộng.
const TierList = ({
  name,
  field,
  min,
  max,
  step = 1,
  precision = 0,
  before = '≥',
  after,
  message,
}: TierListProps) => {
  const { token } = theme.useToken();
  const besideInput = {
    display: 'inline-flex',
    alignItems: 'center',
    height: token.controlHeightLG,
  };
  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {fields.map((item) => (
            <Space key={item.key} align="start" style={{ display: 'flex' }}>
              <Form.Item
                name={[item.name, field]}
                rules={[
                  {
                    required: true,
                    type: precision === 0 ? 'integer' : 'number',
                    min,
                    max,
                    message,
                  },
                ]}
                style={{ marginBottom: 4 }}
              >
                <InputNumber
                  size="large"
                  min={min}
                  max={max}
                  step={step}
                  precision={precision}
                  style={{ width: 170 }}
                  addonBefore={before}
                  addonAfter={after}
                />
              </Form.Item>
              <span style={besideInput}>→</span>
              <Form.Item
                name={[item.name, 'star']}
                rules={[{ required: true, type: 'integer', min: 0, message: 'Sao ≥ 0.' }]}
                style={{ marginBottom: 4 }}
              >
                <InputNumber
                  size="large"
                  min={0}
                  precision={0}
                  style={{ width: 130 }}
                  addonAfter="⭐"
                />
              </Form.Item>
              <span style={besideInput}>
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  aria-label="Xoá mốc"
                  onClick={() => remove(item.name)}
                />
              </span>
            </Space>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => add({ [field]: undefined, star: undefined })}
          >
            Thêm mốc
          </Button>
        </div>
      )}
    </Form.List>
  );
};

const subTitle = (text: string) => (
  <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
    {text}
  </Typography.Title>
);

// Cấu hình Điểm Sao — chỉ ADMIN (GET/PUT /stars/admin/config).
const StarConfigPage = () => {
  const { loaded, isAdmin } = useCurrentRoles();
  const [form] = Form.useForm<StarConfig>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<StarConfig | null>(null);
  // GET có trả `claimLimits` không — máy chủ cũ không trả thì không gửi nhóm này khi PUT.
  const [serverHasClaimLimits, setServerHasClaimLimits] = useState(true);
  const { token } = theme.useToken();
  // Mũi tên "→" và nút xoá mốc căn giữa theo chiều dọc với ô nhập size="large" (hàng căn "start"
  // để khi ô báo lỗi cao thêm thì chúng vẫn nằm ngang ô, không trôi xuống).
  const besideInput = {
    display: 'inline-flex',
    alignItems: 'center',
    height: token.controlHeightLG,
  };

  useEffect(() => {
    if (!loaded) return undefined;
    if (!isAdmin) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.starsConfig);
        const data = normalize(withDefaults(res?.data));
        if (!cancelled) {
          setServerHasClaimLimits(!!res?.data?.claimLimits);
          setSaved(data);
          form.setFieldsValue(data);
        }
      } catch (err) {
        if (!cancelled) notification.error({ message: mapStarError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [loaded, isAdmin, form]);

  const save = async (values: StarConfig) => {
    setSaving(true);
    try {
      const payload: StarConfigPayload = serverHasClaimLimits
        ? values
        : withoutClaimLimits(values);
      const res: any = await AxiosUtils.put(API.starsConfig, payload);
      const data = normalize(withDefaults(res?.data, values));
      setSaved(data);
      form.setFieldsValue(data);
      notification.success({
        message: serverHasClaimLimits
          ? 'Đã lưu cấu hình Điểm Sao.'
          : 'Đã lưu cấu hình Điểm Sao. Máy chủ chưa hỗ trợ nhóm giới hạn nộp tay nên nhóm đó chưa được lưu.',
      });
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setSaving(false);
    }
  };

  const onFinish = (raw: StarConfig) => {
    // Gửi ĐỦ mọi nhóm (kể cả nhóm không có trên form) để máy chủ không phải đoán.
    const values = normalize(withDefaults(raw, saved || DEFAULT_CONFIG));
    if (!values.kcTiers.length) {
      notification.warning({ message: 'Cần ít nhất một mốc KC.' });
      return;
    }
    for (let i = 1; i < values.kcTiers.length; i += 1) {
      if (values.kcTiers[i].kc === values.kcTiers[i - 1].kc) {
        notification.warning({ message: `Mốc KC ${formatNumber(values.kcTiers[i].kc)} bị trùng.` });
        return;
      }
    }
    for (let i = 1; i < values.kcDaysMonth.length; i += 1) {
      if (values.kcDaysMonth[i].days === values.kcDaysMonth[i - 1].days) {
        notification.warning({ message: `Mốc ${values.kcDaysMonth[i].days} ngày bị trùng.` });
        return;
      }
    }
    const problem =
      tierProblem(values.tenure, 'days', 'Thâm niên', (v) => `${v} ngày`) ||
      tierProblem(values.kcGrowth.tiers, 'pct', 'Vượt chính mình', (v) => `+${v}%`) ||
      tierProblem(values.dance.practiceTiers, 'score', 'Buổi tập nhảy', (v) => `${v} điểm`) ||
      tierProblem(values.dance.examTiers, 'score', 'Kiểm tra bài nhảy', (v) => `${v} điểm`) ||
      recruitProblem(values.recruit);
    if (problem) {
      notification.warning({ message: problem });
      return;
    }
    const tiersChanged =
      !saved ||
      !sameTiers(values.kcTiers, saved.kcTiers) ||
      !sameDaysTiers(values.kcDaysMonth, saved.kcDaysMonth || []);
    if (tiersChanged) {
      Modal.confirm({
        title: 'Lưu mốc KC mới?',
        content: 'Mốc mới chỉ áp dụng cho ngày chấm sau này. Các ngày đã chấm không được tính lại.',
        okText: 'Lưu',
        cancelText: 'Xem lại',
        onOk: () => save(values),
      });
      return;
    }
    save(values);
  };

  if (!loaded || loading) {
    return (
      <div>
        <PageTitle title="Cấu hình Điểm Sao" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div>
        <PageTitle title="Cấu hình Điểm Sao" />
        <Alert type="warning" showIcon message="Chỉ ADMIN được xem và sửa cấu hình Điểm Sao." />
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Cấu hình Điểm Sao" />
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="custom-form"
        initialValues={DEFAULT_CONFIG}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Mốc kim cương ngày (KC_DAILY)" styles={cardBody}>
              <Typography.Paragraph type="secondary">
                Mỗi ngày lấy mốc cao nhất đạt được, không cộng dồn. Lưu lại sẽ tự sắp theo KC tăng
                dần.
              </Typography.Paragraph>
              <Form.List name="kcTiers">
                {(fields, { add, remove }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {fields.map((field) => (
                      <Space key={field.key} align="start" style={{ display: 'flex' }}>
                        <Form.Item
                          name={[field.name, 'kc']}
                          rules={[{ required: true, type: 'integer', min: 1, message: 'KC ≥ 1.' }]}
                          style={{ marginBottom: 4 }}
                        >
                          <InputNumber<number>
                            size="large"
                            min={1}
                            precision={0}
                            style={{ width: 170 }}
                            addonBefore="≥"
                            addonAfter="KC"
                            formatter={(v) =>
                              v === undefined || v === null ? '' : formatNumber(Number(v))
                            }
                            parser={(v) => Number(String(v || '').replace(/[^\d]/g, ''))}
                          />
                        </Form.Item>
                        <span style={besideInput}>→</span>
                        <Form.Item
                          name={[field.name, 'star']}
                          rules={[{ required: true, type: 'integer', min: 0, message: 'Sao ≥ 0.' }]}
                          style={{ marginBottom: 4 }}
                        >
                          <InputNumber
                            size="large"
                            min={0}
                            precision={0}
                            style={{ width: 130 }}
                            addonAfter="⭐"
                          />
                        </Form.Item>
                        <span style={besideInput}>
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </span>
                      </Space>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ kc: undefined, star: undefined })}
                    >
                      Thêm mốc
                    </Button>
                  </div>
                )}
              </Form.List>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            {/* Tiêu đề ngắn cho vừa một dòng (trước bị cắt "…" ở 1280 và 1440); phần giải thích
                "số ngày đạt KC trong tháng" chuyển xuống đoạn mô tả. */}
            <Card title="Sao đều tay (KC_DAYS_MONTH)" styles={cardBody}>
              <Typography.Paragraph type="secondary">
                Thưởng theo số ngày đạt KC trong tháng: đếm số ngày có KC ≥ mốc thấp nhất; thưởng
                theo mốc số ngày cao nhất đạt được.
              </Typography.Paragraph>
              <Form.List name="kcDaysMonth">
                {(fields, { add, remove }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {fields.map((field) => (
                      <Space key={field.key} align="start" style={{ display: 'flex' }}>
                        <Form.Item
                          name={[field.name, 'days']}
                          rules={[
                            {
                              required: true,
                              type: 'integer',
                              min: 1,
                              max: 31,
                              message: '1–31 ngày.',
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <InputNumber
                            size="large"
                            min={1}
                            max={31}
                            precision={0}
                            style={{ width: 170 }}
                            addonBefore="≥"
                            addonAfter="ngày"
                          />
                        </Form.Item>
                        <span style={besideInput}>→</span>
                        <Form.Item
                          name={[field.name, 'star']}
                          rules={[{ required: true, type: 'integer', min: 0, message: 'Sao ≥ 0.' }]}
                          style={{ marginBottom: 4 }}
                        >
                          <InputNumber
                            size="large"
                            min={0}
                            precision={0}
                            style={{ width: 130 }}
                            addonAfter="⭐"
                          />
                        </Form.Item>
                        <span style={besideInput}>
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </span>
                      </Space>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ days: undefined, star: undefined })}
                    >
                      Thêm mốc
                    </Button>
                  </div>
                )}
              </Form.List>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Chuỗi tuần chấm công (ATTENDANCE_WEEK)" styles={cardBody}>
              <Typography.Paragraph type="secondary">
                Thưởng theo số tuần liên tiếp đủ 6 ngày công: tuần 1 → tuần 4. Tuần 4 trả 5 Sao
                ngay, phần còn lại trả khi tháng đủ công chuẩn.
              </Typography.Paragraph>
              <Row gutter={8}>
                {[0, 1, 2, 3].map((i) => (
                  <Col span={6} key={i}>
                    <Form.Item
                      label={`Tuần ${i + 1}`}
                      name={['weeklyStreak', i]}
                      rules={[
                        { required: true, type: 'integer', min: 0, message: 'Số nguyên ≥ 0.' },
                      ]}
                    >
                      <InputNumber
                        size="large"
                        style={{ width: '100%' }}
                        min={0}
                        precision={0}
                        addonAfter="⭐"
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Form.Item
                label="Cách tính công chuẩn tháng"
                name="standardDaysMode"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value="FIXED">{STANDARD_DAYS_MODE_LABEL.FIXED}</Radio>
                    <Radio value="MONTH_MINUS_4">{STANDARD_DAYS_MODE_LABEL.MONTH_MINUS_4}</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Công chuẩn cố định',
                    'standardDaysFixed',
                    'Dùng khi chọn 26 công mọi tháng.',
                    1,
                    31
                  )}
                </Col>
                <Col span={12}>
                  {numberField('Công chuẩn tháng 2', 'standardDaysFeb', undefined, 1, 29)}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Giới hạn chung" styles={cardBody}>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Trần tặng/nhận mỗi tháng (Sao)',
                    'transferMonthlyCap',
                    'Tổng gửi + tổng nhận của một người.'
                  )}
                </Col>
                <Col span={12}>
                  {numberField(
                    'MOD điều chỉnh tối đa/lần (Sao)',
                    'smodAdjustCap',
                    'ADMIN không bị giới hạn.'
                  )}
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Đổi tối đa mỗi ngày (món)',
                    'shopDailyLimit',
                    '0 = tạm khoá đổi quà.'
                  )}
                </Col>
                <Col span={12}>
                  {numberField(
                    'Đơn chờ giao hết hạn sau (ngày)',
                    'orderExpireDays',
                    'Quá hạn: huỷ đơn và hoàn Sao.',
                    1,
                    365
                  )}
                </Col>
              </Row>
            </Card>
            {/* Doc 10 §4.2–4.3: tầng người + tầng ví của nhiệm vụ nộp tay (CLAIM). */}
            <Card
              title="Nhiệm vụ nộp tay — giới hạn chống spam"
              styles={cardBody}
              style={{ marginTop: 16 }}
            >
              {!serverHasClaimLimits && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Máy chủ đang chạy chưa hỗ trợ nhóm giới hạn này."
                  description="Cần triển khai backend đợt doc 10 (có claimLimits) trước hoặc cùng lúc với bản admin này. Trong lúc đó các ô dưới đây không được gửi khi Lưu cấu hình (máy chủ dùng mặc định trong mã); tải lại trang sau khi backend cập nhật."
                />
              )}
              <Typography.Paragraph type="secondary">
                Áp cho nhiệm vụ tự báo cáo (nộp minh chứng, Quản lý duyệt). Trần Sao tháng tính cả
                phần đang chờ duyệt lúc nộp; nhiệm vụ hệ thống và nhiệm vụ kỳ &quot;Một lần&quot;
                không chịu trần. Giới hạn riêng từng nhiệm vụ (/ngày, /tuần) đặt trong form nhiệm
                vụ. 0 = không giới hạn.
              </Typography.Paragraph>
              {subTitle('Báo cáo gửi tối đa mỗi ngày (mọi nhiệm vụ)')}
              <Row gutter={16}>
                {STAR_GROUPS.map((g) => (
                  <Col span={8} key={g}>
                    {numberField(
                      STAR_GROUP_LABEL[g],
                      ['claimLimits', 'submissionsPerDayByGroup', g],
                      '0 = không giới hạn',
                      0,
                      undefined,
                      '/ngày'
                    )}
                  </Col>
                ))}
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Báo cáo chờ duyệt tối đa',
                    ['claimLimits', 'maxPending'],
                    'Một người, mọi nhiệm vụ. 0 = không giới hạn',
                    0,
                    undefined,
                    'báo cáo'
                  )}
                </Col>
                <Col span={12}>
                  {numberField(
                    'Nộp lại sau khi bị từ chối',
                    ['claimLimits', 'resubmitMax'],
                    'Cùng nhiệm vụ, cùng kỳ. 0 = không giới hạn',
                    0,
                    undefined,
                    'lần'
                  )}
                </Col>
              </Row>
              {subTitle('Trần Sao nộp tay mỗi tháng')}
              <Row gutter={16}>
                {STAR_GROUPS.map((g) => (
                  <Col span={8} key={g}>
                    {numberField(
                      STAR_GROUP_LABEL[g],
                      ['claimLimits', 'monthlyCapByGroup', g],
                      '0 = không giới hạn',
                      0,
                      undefined,
                      '⭐'
                    )}
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Nhân viên</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Ca chuẩn · Tháng chuẩn" styles={cardBody}>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Ca chuẩn (Sao/ngày)',
                    ['shiftOnTime', 'star'],
                    'Ngày Thứ Hai – Thứ Bảy (trừ lễ) mà mọi ca đều đúng giờ và có chấm ra. Không tính bản ghi nhập tay.',
                    0,
                    undefined,
                    '⭐'
                  )}
                </Col>
                <Col span={12}>
                  {numberField(
                    'Tháng chuẩn (Sao/tháng)',
                    ['monthPerfect', 'star'],
                    'Đủ công chuẩn và không ngày nào trễ, về sớm, quên chấm ra — kể cả ngày đã dùng Quỹ trễ.',
                    0,
                    undefined,
                    '⭐'
                  )}
                </Col>
              </Row>
              <Typography.Text type="secondary">
                Chủ nhật và ngày lễ không tính ca chuẩn (đã trả qua tăng ca). Máy chấm sau nửa đêm;
                tháng chuẩn chấm ngày 1 tháng sau.
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Thâm niên (TENURE)" styles={cardBody}>
              <Typography.Paragraph type="secondary">
                Tính từ ngày chấm công đầu tiên (không tính bản ghi nhập tay). Mỗi mốc trả một lần; chỉ trả mốc tròn trong 30 ngày gần nhất (không trả dồn mốc đã qua lâu).
              </Typography.Paragraph>
              <TierList
                name="tenure"
                field="days"
                min={1}
                max={3650}
                after="ngày"
                message="1–3650 ngày."
              />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Creator Studio (Group LIVE Creator, NPC Creator)</Divider>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Lịch live (LIVE_WEEK · LIVE_MONTH)" styles={cardBody}>
              {subTitle('Live đủ tuần')}
              <Typography.Paragraph type="secondary">
                Tuần theo ngày TikTok. Đủ số ngày trong tuần live từ số phút trở lên → thưởng một
                lần/tuần.
              </Typography.Paragraph>
              <Row gutter={16}>
                <Col span={8}>
                  {numberField('Số ngày', ['liveWeek', 'days'], undefined, 1, 7, 'ngày')}
                </Col>
                <Col span={8}>
                  {numberField(
                    'Mỗi ngày live ≥',
                    ['liveWeek', 'minMinutesPerDay'],
                    undefined,
                    1,
                    1440,
                    'phút'
                  )}
                </Col>
                <Col span={8}>
                  {numberField('Thưởng', ['liveWeek', 'star'], undefined, 0, undefined, '⭐')}
                </Col>
              </Row>
              {subTitle('Tháng đủ lịch live')}
              <Typography.Paragraph type="secondary">
                Đủ số ngày live hợp lệ (mỗi ngày từ số phút trở lên) và đủ tổng giờ live trong
                tháng.
              </Typography.Paragraph>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Số ngày live hợp lệ',
                    ['liveMonth', 'days'],
                    undefined,
                    1,
                    31,
                    'ngày'
                  )}
                </Col>
                <Col span={12}>
                  {numberField('Tổng giờ live ≥', ['liveMonth', 'hours'], undefined, 0, 744, 'giờ')}
                </Col>
                <Col span={12}>
                  {numberField(
                    'Ngày live hợp lệ ≥',
                    ['liveMonth', 'minMinutesPerDay'],
                    undefined,
                    1,
                    1440,
                    'phút'
                  )}
                </Col>
                <Col span={12}>
                  {numberField('Thưởng', ['liveMonth', 'star'], undefined, 0, undefined, '⭐')}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Vượt chính mình (KC_GROWTH)" styles={cardBody}>
              <Typography.Paragraph type="secondary">
                So KC tháng này với trung bình các tháng liền trước. Cần mọi tháng nền đều có KC và
                KC tháng này đạt mức sàn. Lấy mốc % cao nhất đạt được; chấm lại chỉ trả phần chênh,
                không thu hồi.
              </Typography.Paragraph>
              <Row gutter={16}>
                <Col span={12}>
                  {numberField(
                    'Số tháng làm nền',
                    ['kcGrowth', 'baseMonths'],
                    undefined,
                    1,
                    12,
                    'tháng'
                  )}
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="KC tháng này tối thiểu"
                    name={['kcGrowth', 'minMonthKc']}
                    rules={[{ required: true, type: 'integer', min: 0, message: 'Số nguyên ≥ 0.' }]}
                  >
                    <InputNumber<number>
                      size="large"
                      min={0}
                      precision={0}
                      style={{ width: '100%' }}
                      addonAfter="KC"
                      formatter={kcFormatter}
                      parser={kcParser}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <TierList
                name={['kcGrowth', 'tiers']}
                field="pct"
                min={1}
                max={1000}
                before="+"
                after="%"
                message="1–1000%."
              />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Tuyển dụng (HR)</Divider>
        <Card title="Thưởng người tuyển Creator Studio (RECRUIT)" styles={cardBody}>
          <Typography.Paragraph type="secondary">
            Thưởng người tuyển được Creator Studio làm thật và ở lại. Ngày live hợp lệ = ngày live
            từ số phút dưới đây trở lên theo file TikTok ngày. Mốc vượt trần tháng không mất mà trả
            vào tháng sau; Sao đã trả không thu hồi.
          </Typography.Paragraph>
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12} xl={6}>
              {subTitle('Luật chung')}
              {numberField(
                'Ngày bắt đầu lùi tối đa',
                ['recruit', 'registerWindowDays'],
                'Chặn "nhận vơ" Creator đã giỏi sẵn.',
                0,
                365,
                'ngày'
              )}
              {numberField(
                'Ngày live hợp lệ ≥',
                ['recruit', 'liveDayMinMinutes'],
                undefined,
                1,
                1440,
                'phút'
              )}
              {numberField(
                'Trần mỗi người tuyển',
                ['recruit', 'monthlyCap'],
                'Sao/tháng; phần vượt trả tháng sau.',
                0,
                undefined,
                '⭐'
              )}
              {numberField(
                'Hạn xác nhận tay',
                ['recruit', 'manualGraceDays'],
                'Số ngày sau hạn mốc còn xác nhận tay được.',
                0,
                365,
                'ngày'
              )}
              {numberField(
                'Chờ dữ liệu TikTok',
                ['recruit', 'dataLagDays'],
                'Chờ file ngày về đủ rồi mới xét Giữ chân.',
                0,
                30,
                'ngày'
              )}
            </Col>
            <Col xs={24} md={12} xl={6}>
              {subTitle('Mốc Đạt chuẩn')}
              {numberField(
                'Trong số ngày đầu',
                ['recruit', 'qualifyWindowDays'],
                undefined,
                1,
                365,
                'ngày'
              )}
              {numberField(
                'Số ngày live hợp lệ ≥',
                ['recruit', 'qualifyLiveDays'],
                undefined,
                0,
                365,
                'ngày'
              )}
              {numberField(
                'Tổng giờ live ≥',
                ['recruit', 'qualifyLiveHours'],
                undefined,
                0,
                8760,
                'giờ'
              )}
              {numberField('Thưởng', ['recruit', 'qualifyStar'], undefined, 0, undefined, '⭐')}
            </Col>
            <Col xs={24} md={12} xl={6}>
              {subTitle('Mốc Giữ chân')}
              {numberField(
                'Xét vào ngày thứ',
                ['recruit', 'retainAtDays'],
                'Đã Đạt chuẩn và vẫn là Creator Studio đang hoạt động.',
                1,
                730,
                'ngày'
              )}
              {numberField(
                'Ngày live hợp lệ ≥',
                ['recruit', 'retainLiveDays'],
                'Đếm từ hết hạn Đạt chuẩn tới ngày xét.',
                0,
                365,
                'ngày'
              )}
              {numberField('Thưởng', ['recruit', 'retainStar'], undefined, 0, undefined, '⭐')}
            </Col>
            <Col xs={24} md={12} xl={6}>
              {subTitle('Mốc Nổi bật')}
              {numberField(
                'Trong số ngày đầu',
                ['recruit', 'standoutWindowDays'],
                undefined,
                1,
                730,
                'ngày'
              )}
              {numberField(
                'Số ngày đạt mốc KC đầu ≥',
                ['recruit', 'standoutKcDays'],
                'Ngày có Sao kim cương ngày (mốc KC thấp nhất).',
                0,
                730,
                'ngày'
              )}
              {numberField('Thưởng', ['recruit', 'standoutStar'], undefined, 0, undefined, '⭐')}
            </Col>
          </Row>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const total =
                (Number(getFieldValue(['recruit', 'qualifyStar'])) || 0) +
                (Number(getFieldValue(['recruit', 'retainStar'])) || 0) +
                (Number(getFieldValue(['recruit', 'standoutStar'])) || 0);
              return (
                <Typography.Text type="secondary">
                  Một Creator đạt cả 3 mốc đem lại tối đa <strong>{formatNumber(total)} ⭐</strong>{' '}
                  cho người tuyển.
                </Typography.Text>
              );
            }}
          </Form.Item>
        </Card>

        <Divider orientation="left">Chấm điểm nhảy</Divider>
        <Card
          title="Giáo viên chấm Group LIVE Creator (DANCE_PRACTICE · DANCE_EXAM)"
          styles={cardBody}
        >
          <Typography.Paragraph type="secondary">
            Thang 10 điểm, bước 0,5. Vắng → 0 Sao; dưới mốc thấp nhất → ghi điểm, không có Sao. Mỗi
            học viên chỉ một buổi có Sao mỗi ngày cho mỗi loại.
          </Typography.Paragraph>
          <Row gutter={[24, 16]}>
            <Col xs={24} lg={12}>
              {subTitle('Buổi tập')}
              <TierList
                name={['dance', 'practiceTiers']}
                field="score"
                min={0}
                max={10}
                step={0.5}
                precision={1}
                after="điểm"
                message="0–10 điểm."
              />
              <div style={{ marginTop: 12 }}>
                {numberField(
                  'Tối đa buổi có Sao mỗi tuần',
                  ['dance', 'practiceMaxPerWeek'],
                  undefined,
                  0,
                  7,
                  'buổi'
                )}
              </div>
            </Col>
            <Col xs={24} lg={12}>
              {subTitle('Kiểm tra bài mới')}
              <TierList
                name={['dance', 'examTiers']}
                field="score"
                min={0}
                max={10}
                step={0.5}
                precision={1}
                after="điểm"
                message="0–10 điểm."
              />
              <div style={{ marginTop: 12 }}>
                {numberField(
                  'Tối đa bài có Sao mỗi tháng',
                  ['dance', 'examMaxPerMonth'],
                  undefined,
                  0,
                  31,
                  'bài'
                )}
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12} xl={6}>
              {numberField(
                'Được chấm lùi tối đa',
                ['dance', 'backdateDays'],
                '0 = chỉ chấm trong ngày.',
                0,
                30,
                'ngày'
              )}
            </Col>
            <Col xs={24} md={12} xl={6}>
              {numberField(
                'Giáo viên sửa được trong',
                ['dance', 'editHours'],
                'Sau đó chỉ Admin / Quản lý tổng huỷ buổi.',
                0,
                720,
                'giờ'
              )}
            </Col>
          </Row>
        </Card>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            padding: '12px 0',
            background: token.colorBgLayout,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <CustomButton
            text="Khôi phục mặc định"
            bgColor="primary-grey"
            onClick={() => form.setFieldsValue(DEFAULT_CONFIG)}
          />
          <CustomButton
            text="Lưu cấu hình"
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
          />
        </div>
      </Form>
    </div>
  );
};

export default StarConfigPage;
